import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import { H4 } from '../../primitives/Typography';
import Link from '../../primitives/Link';
import Spacer from '../../helpers/Spacer';
import {
  colors,
  mixins,
  text,
  spaces,
  fontWeights,
  animations,
} from '../../core/styles/theme/default';
import ImageWithCaption from '../../components/Image/ImageWithCaption';
import { ImageInterface } from '~/types';
import { LinkVariant } from '~/primitives/Link/Link';

const StyledCard = styled.div`
  position: relative;
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  cursor: pointer;

  ${mixins.responsiveStyles({
    height: { zero: 'auto', medium: '100%' },
    'min-height': { zero: 300, medium: 420 },
  })}
`;

const StyledContent = styled.div(
  ({ variant, theme }: { variant?: string; theme?: Theme }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: ${rem(spaces.delta)};
    border: ${rem(1)} solid ${colors.grue300};
    border-radius: ${rem(2)};
    background: ${colors.white};

    ${mixins.MQ('ultra')} {
      padding: ${rem(spaces.epsilon)};
    }

    ${variant === 'full-image' &&
    css`
      background: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.7) 0%,
        rgba(0, 0, 0, 0.1) 100%
      );
      border: none;
      z-index: 1;
    `}

    ${theme?.stylesExtension?.teaserCard?.default?.content};
  `,
);

const StyledTitle = styled(H4)(
  ({ variant }: { variant?: string }) => css`
    margin-bottom: ${rem(spaces.alpha)};
    font-size: ${rem(20)};
    line-height: ${rem(28)};
    overflow-wrap: break-word;

    ${variant === 'news' &&
    css`
      line-height: ${rem(32)};
    `};

    ${mixins.MQ('medium')} {
      margin-bottom: ${rem(spaces.beta)};
      font-size: ${rem(24)};
      ${variant === 'news' &&
      css`
        font-size: ${rem(20)};
      `};

      line-height: ${rem(30)};
      ${variant === 'news' &&
      css`
        line-height: ${rem(29)};
      `};
    }

    ${variant === 'full-image' &&
    css`
      color: ${colors.white};
    `}
  `,
);

const StyledCardMain = styled.div(
  ({ variant }: { variant?: string }) => css`
    flex: 1;
    margin-bottom: ${rem(spaces.eta)};
    font-size: ${rem(18)};
    ${variant === 'news' &&
    css`
      font-size: ${rem(16)};
    `};

    color: ${colors.grey700};

    line-height: ${rem(26)};
    ${variant === 'news' &&
    css`
      line-height: ${rem(25)};
    `};

    ${mixins.MQ('medium')} {
      margin-bottom: ${rem(spaces.epsilon)};
    }

    ${mixins.MQ('ultra')} {
      margin-bottom: ${rem(spaces.eta)};
      line-height: ${rem(28)};
      ${variant === 'news' &&
      css`
        line-height: ${rem(25)};
      `};
    }

    ${variant === 'full-image' &&
    css`
      color: ${colors.white};
    `}
  `,
);

const StyledLink = styled(Link)(
  ({ teaserVariant, theme }: { teaserVariant?: string; theme?: Theme }) => css`
    padding: ${rem(10)} 0;
    margin-bottom: -${rem(10)};
    color: ${colors.nightBlue500};

    .m-richtext & {
      text-decoration: none;
    }

    ${theme?.stylesExtension?.teaserCard?.default?.link};

    ${teaserVariant === 'full-image' &&
    css`
      color: ${colors.white};

      ${theme?.stylesExtension?.teaserCard?.fullImage?.link};
    `}
  `,
);

const StyledImage = styled.div(
  ({ variant }: { variant?: string }) => css`
    position: relative;
    padding-bottom: 50%;
    background: grey
      linear-gradient(
        90deg,
        rgba(37, 55, 100, 0.9) 0%,
        rgba(37, 55, 100, 0.6) 100%
      );

    figure,
    .gatsby-image-wrapper {
      width: 100%;
      height: 100%;
    }

    figure {
      position: absolute;
      top: 0;
      left: 0;
      overflow: hidden;
    }

    ${mixins.MQ('large')} {
      .gatsby-image-wrapper {
        opacity: 1;
        transition: opacity ${animations.default},
          transform ${animations.default};
        transform: scale(1.07);
      }

      .item:hover & {
        .gatsby-image-wrapper {
          opacity: 0.8;
          transform: scale(1);
        }
      }
    }

    ${variant === 'full-image' &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      padding-bottom: 0;
    `}
  `,
);

const StyledTop = styled.div`
  flex-grow: 1;
`;

const StyledDate = styled.time`
  display: block;
  ${mixins.responsiveStyles({
    'padding-bottom': { zero: rem(spaces.gamma) },
  })}

  ${text.p4};
  color: ${colors.nightBlue700};

  ${({ theme }) => theme?.stylesExtension?.teaserCard?.default?.date}
`;

const StyledPreTitle = styled.span`
  display: block;
  ${mixins.responsiveStyles({
    'padding-bottom': { zero: rem(spaces.gamma) },
  })}

  ${text.p4};
  color: ${colors.nightBlue700};
  font-weight: ${fontWeights.regular};
  text-transform: uppercase;
`;

const StyledLogo = styled(ImageWithCaption)`
  width: ${rem(92)};
`;

const TeaserCard = ({ data, variant }: TeaserCardProps) => {
  const {
    date,
    pretitle,
    title,
    teaser,
    teaserImage,
    logo,
    href,
    linkText,
    linkTitle = linkText,
    linkVariant = 'internal',
    trackClick,
  } = data;

  return (
    <>
      <StyledCard
        className="item animate"
        onClick={(e) => {
          trackClick?.();

          if (!(e.target as HTMLElement).closest('a')) {
            e.currentTarget.querySelector('a')?.click();
          }
        }}
      >
        {teaserImage && (
          <StyledImage variant={variant}>
            <ImageWithCaption data={{ ...teaserImage, disableLink: true }} />
          </StyledImage>
        )}
        <StyledContent variant={variant}>
          {logo && (
            <>
              <StyledLogo data={{ image: logo, disableLink: true }} />
              <Spacer space="epsilon" />
            </>
          )}
          <StyledTop>
            {date ? (
              <StyledDate dateTime={date.split('T')[0]}>
                {date.split('T')[0].split('-').reverse().join('/')}
              </StyledDate>
            ) : null}
            {pretitle ? <StyledPreTitle>{pretitle}</StyledPreTitle> : null}
            {title ? (
              <StyledTitle variant={variant}>{title}</StyledTitle>
            ) : null}
            <StyledCardMain variant={variant}>{teaser}</StyledCardMain>
          </StyledTop>
          <StyledLink
            linkText={linkText}
            link={{
              href,
              title: linkTitle,
              ...(linkVariant === 'external' && {
                target: '_blank',
                rel: 'noopener noreferrer',
              }),
            }}
            variant={linkVariant}
            teaserVariant={variant}
          />
        </StyledContent>
      </StyledCard>
    </>
  );
};

export type TeaserCardProps = {
  data: {
    contentful_id?: string;
    pretitle?: string;
    title: string;
    teaser?: string;
    teaserImage?: ImageInterface;
    logo?: ImageInterface['image'];
    belongsToOverview?: {
      slug: string;
    };
    date?: string;
    linkText: string;
    linkTitle?: string;
    linkVariant?: LinkVariant;
    href: string;
    trackClick?: () => void;
  };
  variant?: string;
};

export default TeaserCard;
